import { EnumerationsRole } from 'client'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { useUserClaims } from '~/composables/useUserClaims'
import { useAuthStore } from '~/stores/auth'
import { ODataVisualizationRouteNames } from '~/constants/navigation'
import { NAME } from '~/constants/strings'
import type { RouteNamedMap } from 'vue-router/auto-routes'

export const fallThroughTo404Routing = (): RouteLocationRaw => {
  return {
    name: 'not-found',
    params: {
      all: 'not-found',
    },
  }
}

const pageAccessGuard = ({
  to,
  pathNamesRequiringElevation,
  isElevated,
}: {
  to: RouteLocationNormalized
  pathNamesRequiringElevation: (keyof RouteNamedMap | number)[]
  isElevated: boolean
}) => {
  if (to.name === 'not-found') {
    return
  }

  const toName = to.name && typeof to.name !== 'symbol' ? to.name : ''
  if (!toName || !to.matched.length) {
    return fallThroughTo404Routing()
  }

  const pathNeedsElevation = pathNamesRequiringElevation.includes(toName)

  if (!pathNeedsElevation) {
    return
  }

  if (isElevated) {
    return
  }

  return fallThroughTo404Routing()
}

export const checkServiceDeliverableManageOnePageAccessGuard = (
  to: RouteLocationNormalized
) => {
  const { hasReadWriteDocumentsDeliverablesServiceFunctionPermission } =
    useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-service-deliverable-page'],
    isElevated:
      hasReadWriteDocumentsDeliverablesServiceFunctionPermission.value,
  })
}

export const checkServiceDeliverablePageAndManageAllAccessGuard = (
  to: RouteLocationNormalized
) => {
  const {
    hasReadWriteDocumentsDeliverablesServiceFunctionPermission,
    hasReadDocumentsDeliverablesServiceFunctionPermission,
  } = useUserClaims()

  const hasAccess =
    'serviceDeliverableID' in to.params &&
    to.params.serviceDeliverableID === 'manage'
      ? hasReadWriteDocumentsDeliverablesServiceFunctionPermission.value
      : hasReadDocumentsDeliverablesServiceFunctionPermission.value

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['service-deliverable-pages-and-manage'],
    isElevated: hasAccess,
  })
}

export const checkManageSecurityAccessGuard = (to: RouteLocationNormalized) => {
  const { hasReadWriteClientSettings } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-security'],
    isElevated: hasReadWriteClientSettings.value,
  })
}

export const checkReportsPageAccessGuard = (to: RouteLocationNormalized) => {
  const { hasReadReportsServiceFunctionPermission } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['reports'],
    isElevated: hasReadReportsServiceFunctionPermission.value,
  })
}

export const checkReportsManagementAccessGuard = (
  to: RouteLocationNormalized
) => {
  const { hasReadWriteReportsServiceFunctionPermission } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-reports'],
    isElevated: hasReadWriteReportsServiceFunctionPermission.value,
  })
}

export const checkManageSystemAlertsAccessGuard = (
  to: RouteLocationNormalized
) => {
  const AuthStore = useAuthStore()
  const shouldSeeManageSystemAlerts =
    AuthStore.user?.roleID === EnumerationsRole.BerkshirePSAdmin ||
    AuthStore.user?.roleID === EnumerationsRole.BerkshireSystemAdmin

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-system-alerts'],
    isElevated: shouldSeeManageSystemAlerts,
  })
}

export const checkResourceLibraryAccessGuard = (
  to: RouteLocationNormalized
) => {
  const { shouldSeeResourceLibrary } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['resource-library', 'resource-preview'],
    isElevated: shouldSeeResourceLibrary.value,
  })
}

export const checkDatavizAccessGuard = (to: RouteLocationNormalized) => {
  const { shouldSeeDashboard } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: [
      ODataVisualizationRouteNames.AdverseImpact,
      ODataVisualizationRouteNames.ApplicantFlow,
      ODataVisualizationRouteNames.Employment,
      ODataVisualizationRouteNames.GoalAttainment,
      ODataVisualizationRouteNames.Goals,
      ODataVisualizationRouteNames.IndividualsWithDisabilities,
      ODataVisualizationRouteNames.Overview,
      ODataVisualizationRouteNames.PersonnelActivity,
      ODataVisualizationRouteNames.ProtectedVeterans,

      ODataVisualizationRouteNames.ProjectOverview,
      ODataVisualizationRouteNames.CompetitiveMovements,
      ODataVisualizationRouteNames.NonCompetitiveMovements,
      ODataVisualizationRouteNames.RecruitmentProcessInsights,
      ODataVisualizationRouteNames.WorkforceInsights,
      ODataVisualizationRouteNames.TalentAvailabilityMetrics,
    ],
    isElevated: shouldSeeDashboard.value,
  })
}

export const checkClientManagementAccessGuard = (
  to: RouteLocationNormalized
) => {
  const { shouldSeeClientManagement } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: [
      'manage-clients',
      'edit-client',
      'create-client',
    ],
    isElevated: shouldSeeClientManagement.value,
  })
}

export const checkUserManagementAccessGuard = (to: RouteLocationNormalized) => {
  const { shouldSeeUserManagement } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-users', 'edit-user', 'create-user'],
    isElevated: shouldSeeUserManagement.value,
  })
}

export const checkAAPSummariesAccessGuard = (to: RouteLocationNormalized) => {
  const AuthStore = useAuthStore()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: [NAME.SUMMARIES],
    isElevated: !AuthStore.hideSummaries,
  })
}

export const checkAdminSupportAccessGuard = (to: RouteLocationNormalized) => {
  const { hasReadWriteAdminSupportAccessPermission } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['admin-support-access'],
    isElevated: hasReadWriteAdminSupportAccessPermission.value,
  })
}

export const checkClientSupportAccessGuard = (to: RouteLocationNormalized) => {
  const { hasReadWriteClientSupportAccessPermission } = useUserClaims()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['client-support-access'],
    isElevated: hasReadWriteClientSupportAccessPermission.value,
  })
}

export const checkExecutiveSummaryAccessGuard = (
  to: RouteLocationNormalized
) => {
  const AuthStore = useAuthStore()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['project-executive-summary'],
    isElevated: AuthStore.isBerkshireRole,
  })
}

export const checkFeatureFlagManagementAccessGuard = (
  to: RouteLocationNormalized
) => {
  const AuthStore = useAuthStore()

  return pageAccessGuard({
    to,
    pathNamesRequiringElevation: ['manage-feature-flags'],
    isElevated: AuthStore.isBerkshireRole,
  })
}
